/**
 * The timeline rows, shared by the Timeline screen and the Home module.
 *
 * Layout is the reference mock: a 44px mono gutter carrying the time, then a
 * title and a quieter detail line, separated by hairlines. Scores in the
 * viewer's own team's colour, everything else in `fg`.
 */

import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'
import type { TimelineEvent } from '@shared/types'

const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
const WEEKDAY_SHORT = new Intl.DateTimeFormat(undefined, { weekday: 'short' })

function bareTime(at: number): string {
  return HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')
}

/**
 * A week spans several days, so the gutter has to carry the day as well as the
 * clock — otherwise Thursday's chip-in and Sunday's touchdown read as the same
 * afternoon. Today drops the day, because on the day itself it is noise.
 */
function stamp(at: number, now: number): { top: string; bottom: string | null } {
  const sameDay = new Date(at).toDateString() === new Date(now).toDateString()
  return sameDay
    ? { top: bareTime(at), bottom: null }
    : { top: WEEKDAY_SHORT.format(at), bottom: bareTime(at) }
}

export function TimelineRow({ event, now }: { event: TimelineEvent; now: number }) {
  const s = stamp(event.at, now)
  // A shout is the only row whose detail is somebody talking rather than the
  // app describing something. It gets quotation marks, full `fg` rather than
  // the quiet `dim` the other details use, and a third again the size — the
  // point of the module is to carry what people said, and at 13px grey it read
  // as a footnote to its own headline.
  const spoken = event.kind === 'shout'
  const body = (
    <>
      <div
        className="mono"
        style={{ fontSize: 10, color: 'var(--dim)', width: 44, flex: '0 0 auto', paddingTop: 3 }}
      >
        <div>{s.top}</div>
        {s.bottom ? <div style={{ marginTop: 2 }}>{s.bottom}</div> : null}
      </div>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <div
          style={{
            fontSize: 14, fontWeight: 600,
            color: event.highlight ? 'var(--bright)' : 'var(--fg)',
          }}
        >
          {event.emoji ? `${event.emoji} ` : ''}{event.title}
        </div>
        {event.detail ? (
          <div
            style={{
              // 13 × 1.3, rounded.
              fontSize: spoken ? 17 : 13,
              color: spoken ? 'var(--fg)' : 'var(--dim)',
              marginTop: spoken ? 5 : 3,
              lineHeight: 1.4,
              overflowWrap: spoken ? 'anywhere' : undefined,
            }}
          >
            {spoken ? `“${event.detail}”` : event.detail}
          </div>
        ) : null}
        {event.imageUrl ? (
          <img
            src={event.imageUrl}
            alt=""
            loading="lazy"
            style={{
              // Shorter than the Shouts feed's 420: a timeline is a list of
              // many things and one photograph should not own the screen.
              display: 'block', width: '100%', maxHeight: 260, objectFit: 'contain',
              marginTop: 8, background: 'var(--bg)', borderRadius: 12,
              border: '1px solid var(--line)',
            }}
          />
        ) : null}
      </div>
    </>
  )

  const style: CSSProperties = {
    display: 'flex', gap: 12, padding: '14px 0',
    borderBottom: '1px solid var(--line)', color: 'var(--fg)', minHeight: 44,
  }

  // A row with an edit control is two links side by side, never one inside the
  // other: an anchor may not contain an anchor, and a row that opens the fund
  // screen when you meant to press Edit is worse than either.
  if (event.editHref) {
    const inner: CSSProperties = { ...style, borderBottom: undefined, padding: 0, flex: '1 1 0%', minWidth: 0 }
    return (
      <div style={{ ...style, alignItems: 'flex-start' }}>
        {event.href
          ? <Link to={event.href} style={inner}>{body}</Link>
          : <div style={inner}>{body}</div>}
        <Link
          to={event.editHref}
          aria-label={`Edit — ${event.title}`}
          style={{
            flex: '0 0 auto', alignSelf: 'center',
            display: 'flex', alignItems: 'center', minHeight: 44, padding: '0 2px 0 8px',
            fontSize: 12, fontWeight: 600, color: 'var(--bright)',
          }}
        >
          Edit
        </Link>
      </div>
    )
  }

  return event.href
    ? <Link to={event.href} style={style}>{body}</Link>
    : <div style={style}>{body}</div>
}

export function TimelineList({ events, now }: { events: TimelineEvent[]; now: number }) {
  return (
    <div aria-live="polite">
      {events.map((e) => <TimelineRow key={e.id} event={e} now={now} />)}
    </div>
  )
}
